// Promise-based confirm dialog that replaces native window.confirm()/alert().
//
// Native dialogs are unstyled, block the JS thread, can't be translated by us
// (Chrome's buttons come out in the browser's language, not the app's), are
// suppressible by the browser after a few uses, and are invisible to our RTL
// handling. Usage:
//
//   const confirm = useConfirm();
//   if (await confirm({ title: <translated title>, body: <translated body>, danger: true })) { … }
//
// Mount <ConfirmProvider> once near the root (AppShell does).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';

export interface ConfirmOptions {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Outside the provider (tests, isolated renders): degrade to the native
    // dialog rather than silently confirming a destructive action.
    return async (opts) => window.confirm(String(typeof opts.title === 'string' ? opts.title : ''));
  }
  return ctx;
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [pending, setPending] = useState<Pending | null>(null);
  const confirmBtn = useRef<HTMLButtonElement>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const close = useCallback((ok: boolean) => {
    setPending((p) => {
      p?.resolve(ok);
      return null;
    });
  }, []);

  // Escape cancels; initial focus lands on the confirm button (or cancel for
  // destructive actions, so a stray Enter can't delete).
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };
    document.addEventListener('keydown', onKey);
    confirmBtn.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [pending, close]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="presentation">
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={() => close(false)}
            className="absolute inset-0 bg-slate-900/40"
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby={pending.body ? 'confirm-body' : undefined}
            className="relative z-10 w-full max-w-sm rounded-card border border-slate-200 bg-white p-5 shadow-lg dark:border-slate-800 dark:bg-slate-900"
          >
            <h2 id="confirm-title" className="text-sm font-bold text-ink dark:text-slate-100">{pending.title}</h2>
            {pending.body && (
              <div id="confirm-body" className="mt-2 text-xs text-ink-2 dark:text-slate-300">{pending.body}</div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-bold text-ink-2 hover:bg-surface dark:border-slate-700 dark:text-slate-200"
              >
                {pending.cancelLabel ?? t('common.cancel')}
              </button>
              <button
                ref={pending.danger ? undefined : confirmBtn}
                type="button"
                onClick={() => close(true)}
                className={cn(
                  'rounded-lg px-3.5 py-2 text-xs font-bold text-white',
                  pending.danger ? 'bg-danger hover:bg-danger/90' : 'bg-primary hover:bg-primary/90',
                )}
              >
                {pending.confirmLabel ?? (pending.danger ? t('common.delete') : t('common.confirm'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
